/* eslint-disable quotes */
/**
 * Step Six: Confirmation
 *
 * Post-booking confirmation. A rosy hero holds the booking summary card
 * (services + add-ons, date/time, location, arrival window) beside a lifestyle
 * photo, followed by a two-column band: per-service "things to know" prep tips
 * on the left (rendered only for booked services that have guidance, and hidden
 * entirely when none do) and a cobalt app-download CTA on the right. The shared
 * site Footer carries the "book your glam on the go" section and wordmark.
 *
 * Layout collapses to a single column at 1024px: the hero photo drops below the
 * card and the app CTA stacks full-width beneath the prep tips.
 */
/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import Image from 'next/image'
import {
  trackBooking,
  trackAppDownloadClicked,
  formatDateLong,
  formatTimeShort,
  getArrivalWindow,
  getAppointmentEndTime,
  confirmationServiceDetails,
  mq
} from '../../../helpers'
import { gsColors as c } from '../../../styles/Variables'
import Footer from '../../footer'
import { setCode } from '../../../pages/_app'
import { useMe } from '../../../services/GraphQlClientService'

const appUrl = 'https://apps.apple.com/us/app/glamsquad/id766676007?ign-mpt=uo%3D4'

const NEXT_PUBLIC_ENVIRONMENT = process.env.NEXT_PUBLIC_ENVIRONMENT
const isDevelopment = NEXT_PUBLIC_ENVIRONMENT === 'development'

const ConfirmationContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  /* --- Hero --- */
  .hero {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    background: ${c.salmon};

    ${mq('max', '1024px')} {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .hero-copy {
    padding: 60px 88px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    ${mq('max', '1200px')} {
      padding: 56px 48px;
    }

    ${mq('max', '1024px')} {
      padding: 40px 24px 24px;
    }

    ${mq('max', '768px')} {
      padding: 32px 16px 20px;
    }
  }

  .hero-heading {
    font-family: Garnett;
    font-weight: 700;
    font-size: 63px;
    line-height: 1.05;
    letter-spacing: 0.63px;
    text-transform: uppercase;
    color: ${c.cobalt};
    margin: 0;

    ${mq('max', '1200px')} {
      font-size: 48px;
    }

    ${mq('max', '768px')} {
      font-size: 28px;
      letter-spacing: 0.28px;
    }
  }

  .hero-intro {
    font-family: Euclid;
    font-weight: 400;
    font-size: 22px;
    line-height: 1.38;
    color: ${c.black};
    margin: 24px 0 32px;
    max-width: 480px;

    ${mq('max', '768px')} {
      font-size: 16px;
      margin: 16px 0 20px;
      max-width: none;
    }
  }

  .hero-image {
    position: relative;
    min-height: 715px;

    ${mq('max', '1024px')} {
      min-height: 0;
      aspect-ratio: 393 / 300;
      order: 3; /* photo sits below the card once stacked */
    }
  }

  /* --- Booking summary card --- */
  .booking-card {
    background: ${c.white};
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    max-width: 548px;

    ${mq('max', '1024px')} {
      max-width: 560px;
    }

    ${mq('max', '768px')} {
      padding: 24px;
      gap: 20px;
      border-radius: 4px;
      max-width: none;
    }
  }

  .card-service-name {
    font-family: Garnett;
    font-weight: 600;
    font-size: 24px;
    line-height: 1.38;
    letter-spacing: 0.24px;
    color: ${c.black};
    margin: 0;

    ${mq('max', '768px')} {
      font-size: 20px;
    }
  }

  .card-service-addons {
    font-family: Euclid;
    font-weight: 400;
    font-size: 18px;
    line-height: 1.4;
    color: ${c.darkGrey};
    margin: 6px 0 0;

    ${mq('max', '768px')} {
      font-size: 16px;
    }
  }

  .card-rows {
    display: flex;
    flex-wrap: wrap;
    gap: 16px 24px;
  }

  .card-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    flex: 1 1 200px;

    img {
      flex-shrink: 0;
      margin-top: 1px;
    }
  }

  .card-row-text {
    display: flex;
    flex-direction: column;
    font-family: Euclid;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.4;
    color: ${c.black};
  }

  /* shares the .rule base style; only toggles visibility per breakpoint */
  .card-divider {
    display: none;

    ${mq('max', '768px')} {
      display: block;
    }
  }

  .card-arrival {
    font-family: Euclid;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.38;
    color: ${c.black};
    margin: 0;

    b {
      font-weight: 600;
    }

    ${mq('max', '768px')} {
      color: ${c.cobalt};
    }
  }

  /* --- Content band: things to know + app CTA --- */
  .content {
    display: flex;
    align-items: flex-start;
    gap: 48px;
    padding: 72px 88px;
    max-width: 1440px;
    width: 100%;
    margin: 0 auto;

    &.cta-only {
      justify-content: center;
    }

    ${mq('max', '1200px')} {
      padding: 56px 48px;
    }

    ${mq('max', '1024px')} {
      flex-direction: column;
      align-items: stretch;
      gap: 40px;
      padding: 40px 24px;
    }

    ${mq('max', '768px')} {
      padding: 32px 16px;
    }
  }

  .things-to-know {
    flex: 1;
    min-width: 0;
  }

  .ttk-heading {
    font-family: Garnett;
    font-weight: 700;
    font-size: 36px;
    line-height: 1.38;
    letter-spacing: 0.36px;
    color: ${c.black};
    margin: 0 0 32px;

    ${mq('max', '768px')} {
      font-size: 24px;
      margin: 0 0 23px;
    }
  }

  .service-group {
    display: flex;
    flex-direction: column;
    gap: 32px;
    margin-top: 32px;

    ${mq('max', '768px')} {
      gap: 18px;
      margin-top: 18px;
    }
  }

  .service-group-name {
    font-family: Garnett;
    font-weight: 600;
    font-size: 20px;
    line-height: 1.38;
    letter-spacing: 0.2px;
    text-transform: uppercase;
    color: ${c.cobalt};
    margin: 0;

    ${mq('max', '768px')} {
      font-size: 18px;
    }
  }

  .rule {
    height: 1px;
    background: ${c.dividerGrey};
    border: 0;
    margin: 0;
    width: 100%;
  }

  .tip-row {
    display: flex;
    gap: 24px;
    align-items: flex-start;

    ${mq('max', '768px')} {
      flex-direction: column;
      gap: 4px;
    }
  }

  .tip-title {
    font-family: Euclid;
    font-weight: 700;
    font-size: 20px;
    line-height: 1.38;
    color: ${c.black};
    margin: 0;
    flex-shrink: 0;
    width: 241px;

    ${mq('max', '768px')} {
      font-weight: 600;
      font-size: 18px;
      width: auto;
    }
  }

  .tip-body {
    font-family: Euclid;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.45;
    color: ${c.darkGrey};
    margin: 0;
    flex: 1;

    ${mq('max', '768px')} {
      font-size: 14px;
    }
  }

  /* --- App download CTA --- */
  .app-cta {
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    flex: 0 0 460px;
    min-height: 502px;
    background: ${c.cobalt};
    padding: 49px 56px 0;

    ${mq('max', '1024px')} {
      flex: 1 1 auto;
      width: 100%;
      min-height: 460px;
      padding: 44px 32px 0;
    }

    ${mq('max', '768px')} {
      padding: 40px 24px 0;
    }
  }

  .app-cta-mark {
    width: 67px;
    height: 67px;
    margin-bottom: 12px;
  }

  .app-cta-heading {
    font-family: Garnett;
    font-weight: 600;
    font-size: 28px;
    line-height: 1.38;
    color: ${c.white};
    margin: 0 0 8px;

    ${mq('max', '768px')} {
      font-size: 22px;
    }
  }

  .app-cta-body {
    font-family: Euclid;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.4;
    color: ${c.white};
    margin: 0 0 28px;
    max-width: 420px;

    ${mq('max', '768px')} {
      font-size: 14px;
    }
  }

  .app-cta-button {
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    justify-content: center;
    background: ${c.salmon};
    color: ${c.cobalt};
    font-family: Euclid;
    font-weight: 600;
    font-size: 16px;
    letter-spacing: 1.28px;
    text-decoration: none;
    padding: 14px 50px;
    text-transform: capitalize;

    ${mq('max', '768px')} {
      font-size: 14px;
      letter-spacing: 1.12px;
      padding: 14px 16px;
    }
  }

  .app-cta-phones {
    margin-top: auto;
    padding-top: 40px;
    width: 100%;

    img {
      display: block;
      width: 100%;
      max-width: 460px;
      height: auto;
      margin: 0 auto;
    }
  }
`

/**
 * Cobalt "download the app" promo card. Reused in the content band (beside the
 * prep tips, or on its own when there are none).
 * @param {{ market?: object }} props
 */
const AppCta = ({ market }) => (
  // app-install-prompt: the App Store link is iOS-only, so this is hidden for
  // non-iOS mobile users (see GlobalStyles / deviceDetection).
  <aside className="app-cta app-install-prompt">
    <img className="app-cta-mark" src="/images/confirmation/gs-mark.png" alt="" aria-hidden="true" />
    <h2 className="app-cta-heading">Get real-time updates</h2>
    <p className="app-cta-body">
      Directly message pros, track ETAs, and gain access to exclusive offers all on the Glamsquad app.
    </p>
    <a
      className="app-cta-button"
      href={appUrl}
      target="_blank"
      rel="noreferrer"
      onClick={() =>
        trackAppDownloadClicked({ placement: 'booking_confirmation', market: market?.shortName })
      }
    >
      Download The App
    </a>
    <div className="app-cta-phones">
      <Image
        src="/images/confirmation/app-phones.png"
        alt="The Glamsquad app shown on smartphones"
        width={1197}
        height={525}
        sizes="(max-width: 1024px) 90vw, 460px"
      />
    </div>
  </aside>
)

AppCta.propTypes = {
  market: PropTypes.object
}

const Six = ({ state, appDispatch, code }) => {
  const {
    bookedAppointment,
    isNewUser,
    appliedPromoCode,
    appliedPromoCodeAmt,
    marketServicesForMarket,
    serviceRequests,
    market
  } = state

  const { timezone } = market

  const me = useMe()
  const dayViewEnabled =
    bookedAppointment.dayViewBooking ??
    state.dayViewBooking ??
    Boolean(state.selectedAppointment?.bookingTokens?.length)

  // trackBooking emits the purchase/order-completed revenue events; fire it exactly once. Without this
  // guard, a `me` re-fetch while this step is mounted would
  // re-run the effect and double-count the order.
  const bookingTrackedRef = useRef(false)
  useEffect(() => {
    if (!me) {
      return
    }
    if (!bookingTrackedRef.current) {
      bookingTrackedRef.current = true
      trackBooking(
        bookedAppointment,
        isNewUser,
        appliedPromoCode,
        appliedPromoCodeAmt,
        marketServicesForMarket,
        serviceRequests,
        market,
        me,
        { fromBookedServices: dayViewEnabled }
      )
    }
  }, [appliedPromoCode, appliedPromoCodeAmt, bookedAppointment, isNewUser, marketServicesForMarket,
    serviceRequests, market, me, dayViewEnabled])

  useEffect(() => {
    return () => {
      appDispatch(setCode(null))
    }
  }, [appDispatch])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // send order confirmation to shopmy for sms attribution
  // test link: https://go.shopmy.us/p-9122579
  useEffect(() => {
    const cookies = document.cookie.split(';').reduce((res, cookie) => ({ ...res, [(cookie.split('=')[0] || '').trim()]: (cookie.split('=')[1] || '').trim() }), {})
    const { sms_click_id } = cookies

    fetch(`https://api.shopmy.us/api/order_confirmation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        orderAmount: bookedAppointment.finalPrice,
        orderId: bookedAppointment.id,
        clickId: sms_click_id,
        currency: 'USD',
        is_returning_customer: false,
        page_url: window.location.href,
        code: appliedPromoCode ?? code ?? ''
      })
    })
      .then(r => {
        if (!r.ok) {
          throw new Error('Invalid order request sent')
        }
        r.json()
      })
      .then(() => {
        const now = new Date()
        document.cookie = `sms_click_id=;expires=${now.toUTCString()};path=/;`
        document.cookie = `sms_click_time=;expires=${now.toUTCString()};path=/;`
      })
      .catch(() => {
        // console.warn('ShopMy order confirmation error', err);
      })
  }, [appliedPromoCode, code, bookedAppointment.id, bookedAppointment.finalPrice])

  const arrivalWindow = getArrivalWindow(bookedAppointment.actualStartDateTime, timezone)
  const appointmentEndTime = getAppointmentEndTime(bookedAppointment, timezone)

  const team = new Set((bookedAppointment?.appointmentServices || []).map(s => s.providerId)).size || 1

  const serviceDetails = confirmationServiceDetails(
    marketServicesForMarket,
    bookedAppointment.appointmentServices,
    market?.id
  )
  const tipGroups = serviceDetails.filter(detail => detail.tips.length > 0)

  // Card header: all booked service names on one line, all add-ons (deduped
  // across services) on the next.
  const serviceNames = serviceDetails.map(detail => detail.name).join(', ')
  const addOnNames = [...new Set(serviceDetails.flatMap(detail => detail.addOnNames))]

  return (
    <div>
      <ConfirmationContainer>
        <section className="hero">
          <div className="hero-copy">
            <h1 className="hero-heading">Get ready for your glam</h1>
            <p className="hero-intro">
              Your confirmation email is on the way.
              {tipGroups.length > 0 && ' In the meantime, check out our tips to prep for your appointment.'}
            </p>

            <div className="booking-card">
              <div className="card-header">
                <p className="card-service-name">{serviceNames}</p>
                {addOnNames.length > 0 && (
                  <p className="card-service-addons">+ {addOnNames.join(', ')}</p>
                )}
              </div>

              <div className="card-rows">
                <div className="card-row">
                  <img src="/images/booking-checkout-calendar.svg" alt="" aria-hidden="true" width={24} height={24} />
                  <div className="card-row-text">
                    <span>{formatDateLong(bookedAppointment.actualStartDateTime, timezone)}</span>
                    <span>{formatTimeShort(bookedAppointment.actualStartDateTime, timezone)}</span>
                  </div>
                </div>
                <div className="card-row">
                  <img src="/images/location-pin-outline.svg" alt="" aria-hidden="true" width={24} height={24} />
                  <div className="card-row-text">
                    {bookedAppointment?.address?.name && <span>{bookedAppointment.address.name}</span>}
                    <span>{bookedAppointment.address.street}</span>
                    {bookedAppointment.address.apartment && <span>{bookedAppointment.address.apartment}</span>}
                    <span>
                      {bookedAppointment.address.city}, {bookedAppointment.address.state} {bookedAppointment.address.zip}
                    </span>
                  </div>
                </div>
              </div>

              <hr className="rule card-divider" />

              <p className="card-arrival">
                Your Beauty Pro{team > 1 ? 's' : ''} will arrive between <b>{arrivalWindow?.[0]}</b> and{' '}
                <b>{arrivalWindow?.[1]}</b> to start the appointment and will finish by <b>{appointmentEndTime}</b>
              </p>
            </div>
          </div>

          <div className="hero-image">
            <Image
              src="/images/confirmation/hero.jpg"
              alt="A Glamsquad client having her hair styled at home"
              fill
              priority
              sizes="(max-width: 1024px) 100vw, 50vw"
              style={{ objectFit: 'cover' }}
            />
          </div>
        </section>

        {tipGroups.length > 0 ? (
          <section className="content">
            <div className="things-to-know">
              <h2 className="ttk-heading">Things to know before your appointment</h2>
              {tipGroups.map(group => (
                <div className="service-group" key={group.serviceId}>
                  <hr className="rule" />
                  <h3 className="service-group-name">{group.name}</h3>
                  {group.tips.map((tip, index) => (
                    <React.Fragment key={`${tip.title}-${index}`}>
                      {index > 0 && <hr className="rule" />}
                      <div className="tip-row">
                        <p className="tip-title">{tip.title}</p>
                        <p className="tip-body">{tip.body}</p>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>
            <AppCta market={market} />
          </section>
        ) : (
          <section className="content cta-only app-install-prompt">
            <AppCta market={market} />
          </section>
        )}
      </ConfirmationContainer>

      <Footer forceShow={true} />

      {!isDevelopment ? (
        <img
          src={`https://www.shareasale.com/sale.cfm?tracking=${bookedAppointment.id}&amount=${bookedAppointment.finalPrice}&merchantID=138044&transtype=sale`}
          width="1"
          height="1"
          alt="share-a-sale pixel"
        />
      ) : null}
    </div>
  )
}

Six.propTypes = {
  state: PropTypes.object.isRequired,
  appDispatch: PropTypes.func.isRequired,
  code: PropTypes.string
}

export default Six
